import { css } from 'linaria';
import { screenHistoryPush, ScreenName } from 'model/helpers/screens-history';
import { Fragment, h } from 'preact';
import { styled } from 'linaria/react';
import { createEffect } from 'effector';
import { useStore } from 'effector-react';
import { useEffect, useState } from 'preact/hooks';

import { Button } from 'ui/atoms/button';
import { Container } from 'ui/container';
import { http } from 'model/helpers/http';
import { Spinner } from 'ui/spinner';
import { Subtitle, Text } from 'ui/typography';
import { countryCode$ } from 'model/main.model';
import { ReloadIcon } from 'ui/icons/reload-icon';
import { MessageIcon } from 'ui/icons/message-icon';
import { countries } from 'libs/phone-input/countries';
import { PhoneInput } from 'libs/phone-input/phone-input';
import { useLocalization } from 'model/localization.model';
import { sendEvent } from 'model/helpers/events.model';
import { StatusBlock } from 'features/fallback/ui/info-block';
import { createLinkFactory } from 'model/flows/fallback.model';
import { ButtonContainer } from 'features/fallback/ui/button-container';

const link = createLinkFactory(
	createEffect({
		handler: async (payload: { value: string, sessionId: string }) => {
			await http.post({
				url: '/cross_device/send_sms',
				body: {
					session_id: payload.sessionId,
					phone: '+' + payload.value,
				},
			});
		},
	}),
);

export const LinkSmsView = () => {
	const t = useLocalization();
	const initialCode = useStore(countryCode$);
	const [countryCode, updateCountryCode] = useState(initialCode);
	const value = useStore(link.value$);
	const isSuccess = useStore(link.isSuccess$);
	const isSmsSending = useStore(link.sendFx.pending);
	const [phone, updatePhone] = useState(value);
	const [start] = useState(Date.now());

	const handleSendAgain = (e: Event) => {
		e.preventDefault();
		// @ts-ignore
		if (e.target.tagName.toLowerCase() !== 'a') { return; }

		link.sendFx(phone);
		sendEvent({ event: 'sms_success_send_again' });
	};

	const handleSendEmail = () => {
		link.sendFx(phone);
		sendEvent({
			event: 'sms_send_clicked',
			event_attributes: {
				geoip_country: initialCode,
				selected_country: countryCode,
				duration: Date.now() - start,
			},
		});
	};

	const handleUseDifferentPhoneNumber = () => {
		link.useDifferent();
		sendEvent({
			event: 'sms_success_use_different',
		});
	};
	const handleTryAgain = () => {
		link.tryAgain();
		sendEvent({ event: 'sms_error_try_again' });
	};

	useEffect(() => {
		setTimeout(() => {
			sendEvent({ event: 'sms_screen_shown' });
		}, 20);
	}, []);
	useEffect(() => {
		if (isSuccess === null) {
			screenHistoryPush(ScreenName.sms);
		}
		if (isSuccess === true) {
			screenHistoryPush(ScreenName.sms_success);
			setTimeout(() => {
				sendEvent({ event: 'sms_success_screen_shown' });
			}, 20);
		}
		if (isSuccess === false) {
			screenHistoryPush(ScreenName.sms_error);
			setTimeout(() => {
				sendEvent({ event: 'sms_error_screen_shown' });
			}, 20);
		}
	}, [isSuccess]);

	return (
		<Container>
			<Subtitle>{t('sms.title')}</Subtitle>
			<Text className={textClass}>{t('sms.description')}</Text>
			{isSuccess === false && (
				<Fragment>
					<StatusBlock status={'warning'}>
						{t('sms.error.msg')}
					</StatusBlock>
					<Button
						type={'link'}
						className={buttonClass}
						style={{ marginBottom: 32 }}
						onClick={handleTryAgain}
						icon={<ReloadIcon/>}
						name={'sms.btn.error'}
					>
						{t('sms.btn.error')}
					</Button>
				</Fragment>
			)}
			{isSuccess === true && (
				<LoadingBlock data-loading={isSmsSending}>
					<StatusBlock status={'success'}>
						{t('sms.success.msg', {
							phone: `<strong>+${phone}</strong>`,
						})}
					</StatusBlock>
					<Text
						onClick={handleSendAgain}
						style={{ textAlign: 'center', marginTop: 8 }}
						dangerouslySetInnerHTML={{
							__html: t('sms.success.hint', {
								'sms.success.hint.sub': `
									<a href="#">
										${t('sms.success.hint.sub')}
									</a>`,
							}),
						}}
					/>
					<Button
						type={'link'}
						className={buttonClass}
						onClick={handleUseDifferentPhoneNumber}
						icon={<ReloadIcon fill={'currentColor'}/>}
						name={'sms.btn.success'}
					>
						{t('sms.btn.success')}
					</Button>
					{isSmsSending && (
						<Spinner
							size={32}
							className={spinner}
							fill={{
								primary: 'var(--primary-color)',
								secondary: 'var(--secondary-color)',
							}}
						/>
					)}
				</LoadingBlock>
			)}
			{isSuccess === null && (
				<Fragment>
					<PhoneInput
						value={phone}
						initialCountry={initialCode}
						preferredCountries={[initialCode]}
						countries={countries as any}
						onChange={(phone, code) => {
							updatePhone(phone);
							updateCountryCode(code);
						}}
						localize={code => t(`countries.${code.toUpperCase()}`)}
					/>
					<ButtonContainer style={{ marginTop: 16 }}>
						<Button
							disabled={phone.length <= 4}
							loading={isSmsSending}
							onClick={handleSendEmail}
							name={'sms.btn'}
							style={{ width: '100%' }}
							icon={
								<MessageIcon/>
							}
						>
							{t('sms.btn')}
						</Button>
					</ButtonContainer>
				</Fragment>
			)}
		</Container>
	);
};

const LoadingBlock = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;

  &[data-loading='true'] {
    & > * {
      opacity: .5;
    }
  }
`;
const textClass = css`
  margin-bottom: 20px;
  min-height: 44px;
`;
const buttonClass = css`
  margin-top: 8px;
  margin-bottom: 8px;
  width: 100%;
`;
const spinner = css`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  animation: spin 1s ease-in-out infinite;
`;
